import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';

import { useGetVariantRules, useMultiNodes, useResolver, useResponsiveCss } from 'craft/hooks';
import { useViewportBreakpoint } from 'craft/state';
import { CssSelectorId } from 'craft/types';

import { getSizeValues, writeValue } from './sizeValues';
import { ResponsiveSize, Size } from './types';
import { useSize } from './useSize';

interface UseResponsiveSize {
  responsiveSize?: ResponsiveSize;
  placeholderSize?: ResponsiveSize;
  setResponsiveSize?: (
    size: Size,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
    property?: 'min' | 'max' | 'normal',
  ) => void;
}

export const useResponsiveSize = (
  nodeIds: NodeId[],
  cssSelectorId?: CssSelectorId,
): UseResponsiveSize => {
  const viewportBreakpoint = useViewportBreakpoint();
  const { responsiveCss } = useResponsiveCss(nodeIds, undefined, cssSelectorId);
  const { css, mediaPropsObj, props, type } = useMultiNodes(nodeIds, (node) => ({
    css: node?.data.custom?.css ?? {},
    props: node?.data.props ?? {},
    mediaPropsObj: node?.data.custom?.media?.props ?? {},
  }));

  const mediaProps = mediaPropsObj && viewportBreakpoint ? mediaPropsObj[viewportBreakpoint] : {};

  const { size, setSize } = useSize(nodeIds, cssSelectorId);
  const resolver = useResolver(type);
  const { variantKey } = resolver?.toolbox || {};

  const entriesProperties = ['minWidth', 'minHeight', 'maxWidth', 'maxHeight'].map((key) => [
    key,
    (mediaProps ?? {})[key] ||
      (responsiveCss ?? {})[key] ||
      (props ?? {})[key] ||
      (css ?? {})[key] ||
      undefined,
  ]);
  const responsiveDimensions = Object.fromEntries(entriesProperties);

  const variantRules = useGetVariantRules(nodeIds);

  if (!css) {
    return { responsiveSize: undefined, setResponsiveSize: undefined };
  }

  const {
    '--width': width,
    '--height': height,
    '--min-width': minWidth,
    '--max-width': maxWidth,
    '--min-height': minHeight,
    '--max-height': maxHeight,
  } = variantRules?.selectors?.[variantKey ?? '']?.rules || {};

  const properties = {
    width: writeValue(size.width),
    height: writeValue(size.height),
    minWidth: responsiveDimensions.minWidth,
    maxWidth: responsiveDimensions.maxWidth,
    minHeight: responsiveDimensions.minHeight,
    maxHeight: responsiveDimensions.maxHeight,
  };

  const placeholderProperties = {
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
  };

  if (!properties) {
    return { responsiveSize: undefined, setResponsiveSize: undefined };
  }

  const responsiveSize: ResponsiveSize = {
    normal: getSizeValues({ width: properties.width ?? '', height: properties.height ?? '' }),
    min: getSizeValues({ width: properties.minWidth, height: properties.minHeight }),
    max: getSizeValues({ width: properties.maxWidth, height: properties.maxHeight }),
  };

  const placeholderSize: ResponsiveSize = {
    normal: getSizeValues({
      width: placeholderProperties.width,
      height: placeholderProperties.height,
    }),
    min: getSizeValues({
      width: placeholderProperties.minWidth,
      height: placeholderProperties.minHeight,
    }),
    max: getSizeValues({
      width: placeholderProperties.maxWidth,
      height: placeholderProperties.maxHeight,
    }),
  };

  return { responsiveSize, placeholderSize, setResponsiveSize: setSize };
};
